import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import CrmDialog from "../ui/CrmDialog";
import CrmInput from "../ui/CrmInput";
import CrmTextarea from "../ui/CrmTextarea";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

/**
 * A nudge's email, "pre-written, editable before send" (spec): the draft the server
 * composed (`GET /api/crm/nudges/{id}/draft` → `{subject, body_text}`) in a subject +
 * body editor. The body stays plain text; the server escapes and renders it
 * (`POST …/action {subject, body_text}`, contract v1.5).
 *
 * `withSubject={false}` (the `pathway_stuck` encouragement, pathways-api-contract §10.8,
 * whose e-mail carries its own subject): the body alone is edited, Send needs a body,
 * and the action receives `{body_text}`.
 *
 * `maxBody` caps the body at the length the server accepts for this action, so an
 * over-long text is stopped at the keyboard instead of coming back as a refusal.
 */
export default function NudgeEmailDialog({ nudgeId, title, withSubject = true, maxBody, onClose, onSend }) {
  const t = useT("crm");
  const tc = useT("common");
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    crmApi.getNudgeDraft(nudgeId)
      .then((d) => { if (alive) setDraft({ subject: d?.subject || "", body: d?.body_text || "" }); })
      .catch((e) => { if (alive) setError(e); });
    return () => { alive = false; };
  }, [nudgeId]);

  const send = async () => {
    setBusy(true);
    try {
      await onSend(withSubject ? { subject: draft.subject.trim(), body_text: draft.body } : { body_text: draft.body });
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmDialog open onOpenChange={(o) => { if (!o) onClose(); }} title={title} testId={CRM.nudgeDialog}
      footer={draft && (
        <>
          <CrmButton variant="link" onClick={onClose}>{t("common.cancel")}</CrmButton>
          <CrmButton onClick={send} disabled={busy || !(withSubject ? draft.subject.trim() : draft.body.trim())} data-testid={CRM.nudgeSend}>{t("common.send")}</CrmButton>
        </>
      )}>
      {error && <CrmLoadFailure error={error} />}
      {draft && (
        <div className="space-y-3">
          {withSubject && (
            <div>
              <label htmlFor="crm-nudge-subject" className={LABEL}>{t("email.subject")}</label>
              <CrmInput id="crm-nudge-subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            </div>
          )}
          <div>
            <label htmlFor="crm-nudge-body" className={LABEL}>{t("email.body")}</label>
            <CrmTextarea id="crm-nudge-body" rows={6} maxLength={maxBody} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          </div>
        </div>
      )}
    </CrmDialog>
  );
}
